import { useState } from "react";
import { Download, Loader2, FileText } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const BROCHURE_URL = "/qwis-brochure.pdf";

export default function BrochureButton({
  variant = "primary",
  label = "Download Brochure",
  size = "md",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const triggerDownload = () => {
    // Force the PDF to download as a file (not open inline in some browsers)
    const a = document.createElement("a");
    a.href = BROCHURE_URL;
    a.download = "Q-Wis-Brochure.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please fill in name, email and company.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/brochure-download", form);
      toast.success("Thank you — your brochure is downloading.");
      triggerDownload();
      setForm({ name: "", email: "", company: "" });
      setTimeout(() => setOpen(false), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start download. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const sizeClasses = size === "lg" ? "px-7 py-4 text-base" : "px-6 py-3.5 text-sm";
  const styleClasses =
    variant === "ghost"
      ? "border border-white/25 bg-white/5 text-white hover:bg-white/10"
      : variant === "outline"
      ? "border border-[#0A1128] bg-transparent text-[#0A1128] hover:bg-[#0A1128] hover:text-white"
      : "bg-[#E8772E] text-white shadow-lg shadow-[#E8772E]/20 hover:bg-[#D66826]";

  return (
    <>
      <Toaster richColors position="top-right" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            data-testid="brochure-button"
            className={`inline-flex items-center gap-2 rounded-md font-semibold transition-all ${sizeClasses} ${styleClasses} ${className}`}
          >
            <Download size={size === "lg" ? 18 : 16} />
            {label}
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md" data-testid="brochure-dialog">
          <DialogHeader>
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0A1128]">
              <FileText size={20} className="text-[#E8772E]" />
            </div>
            <DialogTitle className="mt-4 font-serif-display text-2xl text-[#0A1128]">
              Download the Q-Wis Brochure
            </DialogTitle>
            <DialogDescription className="text-sm text-[#475569]">
              A one-page overview of the four modules, why Q-Wis, and what's included. Tell us a bit about you and the PDF will download instantly.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="mt-4 space-y-4" data-testid="brochure-form">
            <Field label="Full name *">
              <Input
                data-testid="brochure-name"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Your name"
                autoFocus
              />
            </Field>
            <Field label="Work email *">
              <Input
                data-testid="brochure-email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="you@company.com"
              />
            </Field>
            <Field label="Company *">
              <Input
                data-testid="brochure-company"
                value={form.company}
                onChange={onChange("company")}
                placeholder="Company name"
              />
            </Field>

            <button
              data-testid="brochure-submit"
              type="submit"
              disabled={busy}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#E8772E] px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#D66826] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {busy ? "Preparing your download..." : "Download PDF"}
            </button>

            <p className="text-center text-xs text-[#64748B]">
              We'll never share your email. See our{" "}
              <a href="/privacy" className="underline hover:text-[#E8772E]">Privacy Policy</a>.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-[#475569]">
        {label}
      </Label>
      {children}
    </div>
  );
}
